import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';

import { useGetEmployeeSearchQuery } from '../coachingSlice';
import { selectCurrentIsAdmin, selectCurrentIsHC, selectCurrentUserManagerLevel } from '../../../pages/login/authSlice';
import { ROLES } from '../../../constants';

interface ActivityEmployeeSearchPopUpProps {
    visible: boolean;
    loggedEmpNumber: string;
    searchedEmpNumber: any;
    setVisible: (visible: boolean) => void;
    setSelectedTeam: (selectedTeam: string) => void;
    setSearchedEmployee: (searchedEmployee: any) => void;
    onEmployeeSelected: (selectedEmployee: any) => void;
}

export const ActivityEmployeeSearchPopUp = ({
    visible,
    loggedEmpNumber, 
    searchedEmpNumber,
    setVisible,
    setSelectedTeam,
    setSearchedEmployee,
    onEmployeeSelected,
}: ActivityEmployeeSearchPopUpProps) => {
    const [searchTerm, setSearchTerm] = useState<string>('');
    const [noResultMsg, setNoResultMsg] = useState<string>('');
    const [searchResults, setSearchResults] = useState<Array<any>>([]);
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState<string>('');
    const userManagerLevel = useSelector(selectCurrentUserManagerLevel);
    const isAdmin = useSelector(selectCurrentIsAdmin);
    const isHC = useSelector(selectCurrentIsHC);

    const { data: employeeSearchData } = useGetEmployeeSearchQuery(
        {
            searchedEmpNumber: debouncedSearchTerm,
            searcherEmpNo: loggedEmpNumber,
            getAllEmps: (isAdmin || isHC || ROLES.Manager.includes(userManagerLevel)) && true ,
        },
        {
            skip: debouncedSearchTerm.length <= 4 
        }
    ); 

    useEffect(() => {
        let timer: any = null;
        if (searchTerm.length >= 5) {
            timer = setTimeout(() => {
                setDebouncedSearchTerm(searchTerm);
            }, 1000);
        } else resetState();

        return () => {
            if (timer) clearTimeout(timer);
        };
    }, [searchTerm]);

    useEffect(() => {
        if (!visible) {
            resetState();
        }
    }, [visible]);

    useEffect(() => {
        if (employeeSearchData && searchedEmpNumber === null) setNoResultMsg('No results found');
        else setNoResultMsg('');

        setSearchResults(employeeSearchData?.data.length > 0 ? employeeSearchData.data : []);

        if (debouncedSearchTerm.length === 0) resetState();
    }, [debouncedSearchTerm, employeeSearchData]);

    const resetState = () => {
        setSearchResults([]);
        setNoResultMsg('');
        setSearchedEmployee(null);
    };

    const footerContent = (
        <div className="row float-end">
            {/* <div className="col-md-9">&nbsp;</div> */}
            <div className="col-md-3">
                <Button
                    label="Cancel"
                    onClick={() => {
                        localStorage.removeItem('selectedTeam');
                        setSearchTerm('');
                        setSearchResults([]);
                        setNoResultMsg('');
                        setSearchedEmployee(null);
                        setSelectedTeam('');
                        setVisible(false);
                    }}
                    className="btn-secondary"
                />
            </div>
        </div>
    );

    const handleSeletedOption = (option: any) => {
        const jsonString = JSON.stringify(option);
        localStorage.setItem('searchedUser', jsonString);
        setSearchedEmployee(option);
        setSearchTerm('');
        setDebouncedSearchTerm('');
        setSearchResults([]);
        onEmployeeSelected(option);
        setVisible(false); // Ensure to hide the dialog after selecting an employee
        resetState();
    };
    const handleKeyDown = (e: any) => {
        if (e.key === 'Enter' && searchResults.length > 0) handleSeletedOption(searchResults[0]);
    };

    return (
        <Dialog
            header="Employee Oracle ID Search"
            draggable={false}
            className="employee-dialog"
            visible={visible}
            style={{ width: '35vw' }}
            onHide={() => setVisible(false)}
            footer={footerContent}
        >
            <div className="row mt-4 mb-4">
                <div className="col-md-12">
                    <div className="p-input-icon-left" style={{ width: '98%' }}>
                        <i className="pi pi-search" />
                        <InputText
                            keyfilter="pnum"
                            maxLength={10}
                            placeholder="Search"
                            style={{ width: '100%' }}
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                        />
                    </div>

                    {searchResults.length > 0 ? (
                        <ul role="menu" className="suggest-container">
                            {searchResults.map((option: any) => (
                                <li
                                    className="suggest-item"
                                    key={option.employee_number}
                                    role="menuitem"
                                    onClick={() => {
                                        handleSeletedOption(option);
                                        setVisible(false);
                                    }}
                                    onKeyDown={handleKeyDown}
                                >
                                    {`${option.employee_name}-(${option.location}),${option.job_title}`}
                                </li>
                            ))}
                        </ul>
                    ) : (
                        <p style={{ marginTop: '10px' }}>{noResultMsg}</p>
                    )}
                </div>
            </div>
        </Dialog>
    );
};
